/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useDispatch, useSelector } from 'react-redux';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';

import { createWorkspace } from 'src/actions/workspaces';
import NullWorkspace from 'src/entities/nullWorkspace';
import { getCurrentUserOwnWorkspace } from 'src/selectors/workspaces';
import PricingSubscriptionPresenter from 'src/presenters/PlanSettings/PricingSubscriptionPresenter';
import PricingPlanPrice from 'src/entities/pricing/pricingPlanPrice';
import { useCallback, useMemo, useState } from 'react';
import Workspace from 'src/entities/workspace';
import useFetchCurrentUserQuery from 'src/new_arch/modules/authentication/queries/useFetchCurrentUserQuery';

const useSubsciptionPlanSelectionModal = ( { onClose }: { onClose: ( workspaceCreated: boolean ) => void } ) => {
	const [ selectedPricingPlanPrice, setSelectedPricingPlanPrice ] = useState<PricingPlanPrice | null>( null );
	const [ isShowingCheckoutModal, setIsShowingCheckoutModal ] = useState( false );
	const [ isCreatingWorkspace, setIsCreatingWorkspace ] = useState( false );
	const snackbarSystem = useSnackbarSystem();

	const dispatch = useDispatch();
	const { data: currentUser } = useFetchCurrentUserQuery();
	// @ts-expect-error
	const workspace: Workspace = useSelector( state => getCurrentUserOwnWorkspace( state, { currentUser } ) )
		|| new NullWorkspace();

	const createWorkspaceAction = ( ...args: unknown[] ) => ( dispatch(
		createWorkspace( ...args )
	) as unknown as Promise<unknown> );

	const pricingSubscriptionPresenter = useMemo(
		() => new PricingSubscriptionPresenter( { workspace } ),
		[ workspace ]
	);

	const showWorkspaceCreationError = useCallback( () => {
		snackbarSystem.showErrorMessage( {
			title: 'Failed to start a workspace with the selected plan. Please try again.'
		} );
	}, [ snackbarSystem ] );

	const onNoWorkspaceButtonClicked = useCallback( ( aPricingPlanPrice: PricingPlanPrice ) => {
		setIsCreatingWorkspace( true );
		createWorkspaceAction( aPricingPlanPrice )
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
			.then( ( response: any ) => {
				if ( response && !response.error ) {
					onClose( true );
				} else {
					showWorkspaceCreationError();
				}
			} )
			.catch( () => showWorkspaceCreationError() )
			.finally( () => setIsCreatingWorkspace( false ) );
	}, [ createWorkspaceAction, onClose, showWorkspaceCreationError ] );

	const onUpgradeButtonClicked = useCallback( ( aPricingPlanPrice: PricingPlanPrice ) => {
		setSelectedPricingPlanPrice( aPricingPlanPrice );
		setIsShowingCheckoutModal( true );
	}, [ setSelectedPricingPlanPrice, setIsShowingCheckoutModal ] );

	const closeCheckoutModal = useCallback( () => {
		setIsShowingCheckoutModal( false );
	}, [ setIsShowingCheckoutModal ] );

	const onSuccessfulPaymentDialogAccepted = useCallback( () => {
		setIsShowingCheckoutModal( false );
		onClose( true );
	}, [ setIsShowingCheckoutModal ] );

	return {
		workspace,
		pricingSubscriptionPresenter,
		onUpgradeButtonClicked,
		onNoWorkspaceButtonClicked,
		selectedPricingPlanPrice,
		closeCheckoutModal,
		onSuccessfulPaymentDialogAccepted,
		isShowingCheckoutModal,
		isCreatingWorkspace
	};
};

export default useSubsciptionPlanSelectionModal;
